// Expressive strokes (Figma Draw): variable width, dynamic (wobbly) lines, stretch brushes and
// scatter brushes. Each turns a node's stroke path into filled polygons, so every renderer and
// export draws them the same way. Random variation is seeded per layer, so it never flickers.

import type { NodeData, StrokeBrush } from './types.ts';
import { flattenPath, type PathCmd } from './geometry.ts';

type Pt = { x: number; y: number };

export interface DrawStroke {
  polygons: Pt[][];
  rule: 'nonzero' | 'evenodd';
}

/** Whether a node's stroke needs this module (otherwise the canvas strokes the path itself). */
export function hasDrawStroke(n: Pick<NodeData, 'strokeWidthProfile' | 'strokeDynamic' | 'strokeBrush'>): boolean {
  return !!(n.strokeWidthProfile?.length || (n.strokeDynamic && n.strokeDynamic.wiggle > 0) || n.strokeBrush);
}

const P = (...pts: [number, number][]) => pts.map(([position, width]) => ({ position, width }));
/** Width profiles, by Figma's preset names (lowercase, dashes). */
export const WIDTH_PROFILES: Record<string, { position: number; width: number }[]> = {
  uniform: P([0, 1], [1, 1]),
  wedge: P([0, 1], [1, 0]),
  taper: P([0, 1], [0.6, 1], [1, 0]),
  'quarter-taper': P([0, 1], [0.75, 1], [1, 0]),
  eye: P([0, 0], [0.5, 1], [1, 0]),
  'mirrored-taper': P([0, 0], [0.25, 1], [0.75, 1], [1, 0]),
};

export const STRETCH_PRESETS = ['calligraphy', 'marker', 'taper', 'ink'];
export const SCATTER_PRESETS = ['dots', 'stars', 'squares', 'confetti'];

/** Figma's named brushes, drawn with the closest built-in style. */
export const FIGMA_STRETCH_BRUSHES: Record<string, string> = {
  HEIST: 'ink', BLOCKBUSTER: 'marker', GRINDHOUSE: 'ink', BIOPIC: 'calligraphy', SPAGHETTI_WESTERN: 'ink', SLASHER: 'calligraphy', HARDBOILED: 'marker', VERITE: 'marker',
  EPIC: 'calligraphy', SCREWBALL: 'ink', ROM_COM: 'taper', NOIR: 'ink', PROPAGANDA: 'marker', MELODRAMA: 'taper', NEW_WAVE: 'calligraphy',
};
export const FIGMA_SCATTER_BRUSHES: Record<string, string> = {
  BUBBLEGUM: 'dots', WITCH_HOUSE: 'stars', SHOEGAZE: 'dots', HONKY_TONK: 'stars', SCREAMO: 'confetti', DRONE: 'dots', DOO_WOP: 'dots', SPOKEN_WORD: 'squares', VAPORWAVE: 'squares', OI: 'confetti',
};

// ---------------------------------------------------------------- noise helpers

function rng(seed: number) {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const hash1 = (i: number, seed: number) => {
  let h = Math.imul(i ^ seed, 0x45d9f3b) ^ 0x9e3779b9;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

/** Smooth 1-D value noise in -1..1. */
function noise1(x: number, seed: number): number {
  const i = Math.floor(x),
    f = x - i;
  const u = f * f * (3 - 2 * f);
  return (hash1(i, seed) * (1 - u) + hash1(i + 1, seed) * u) * 2 - 1;
}

/** A stable seed from a node id. */
export function seedOf(id: string, extra = 0): number {
  let h = 2166136261 ^ extra;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

// ---------------------------------------------------------------- sampling

interface Sampled {
  pts: Pt[];
  /** arc length at each point */
  s: number[];
  closed: boolean;
}

function resample(points: Pt[], closed: boolean, step: number): Sampled {
  const src = closed && points.length > 1 ? [...points, points[0]] : points;
  const pts: Pt[] = [];
  const s: number[] = [];
  if (src.length < 2) return { pts: src.slice(), s: src.map(() => 0), closed };
  let total = 0;
  pts.push(src[0]);
  s.push(0);
  let carry = 0;
  for (let i = 1; i < src.length; i++) {
    const a = src[i - 1],
      b = src[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len === 0) continue;
    let d = step - carry;
    while (d <= len) {
      const t = d / len;
      pts.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      s.push(total + d);
      d += step;
    }
    carry = len - (d - step);
    total += len;
  }
  const last = src[src.length - 1];
  if (Math.hypot(last.x - pts[pts.length - 1].x, last.y - pts[pts.length - 1].y) > 1e-6) {
    pts.push(last);
    s.push(total);
  }
  return { pts, s, closed };
}

function tangents(p: Pt[], closed: boolean): number[] {
  return p.map((_, i) => {
    const a = p[i > 0 ? i - 1 : closed ? p.length - 2 : 0];
    const b = p[i < p.length - 1 ? i + 1 : closed ? 1 : p.length - 1];
    return Math.atan2(b.y - a.y, b.x - a.x);
  });
}

function profileAt(profile: { position: number; width: number }[] | undefined, t: number): number {
  if (!profile?.length) return 1;
  const ps = [...profile].sort((a, b) => a.position - b.position);
  if (t <= ps[0].position) return ps[0].width;
  for (let i = 1; i < ps.length; i++) {
    if (t <= ps[i].position) {
      const a = ps[i - 1],
        b = ps[i];
      const k = (t - a.position) / (b.position - a.position || 1);
      return a.width + (b.width - a.width) * k;
    }
  }
  return ps[ps.length - 1].width;
}

// ---------------------------------------------------------------- shapes for scatter brushes

function unitShape(preset: string): Pt[] {
  const circle = (n: number) => Array.from({ length: n }, (_, i) => ({ x: 0.5 * Math.cos((i / n) * Math.PI * 2), y: 0.5 * Math.sin((i / n) * Math.PI * 2) }));
  switch (preset) {
    case 'stars':
      return Array.from({ length: 10 }, (_, i) => {
        const r = i % 2 ? 0.2 : 0.5;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        return { x: r * Math.cos(a), y: r * Math.sin(a) };
      });
    case 'squares':
      return [
        { x: -0.5, y: -0.5 },
        { x: 0.5, y: -0.5 },
        { x: 0.5, y: 0.5 },
        { x: -0.5, y: 0.5 },
      ];
    case 'confetti':
      return [
        { x: -0.5, y: -0.18 },
        { x: 0.5, y: -0.18 },
        { x: 0.5, y: 0.18 },
        { x: -0.5, y: 0.18 },
      ];
    default:
      return circle(16);
  }
}

/** A source layer's outline as polygons, normalized to a unit box centered on 0 (scatter) or kept (stretch). */
export type SourceOutline = (id: string) => Pt[][] | null;

function normalize(polys: Pt[][]): Pt[][] {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of polys) for (const q of p) (x0 = Math.min(x0, q.x)), (y0 = Math.min(y0, q.y)), (x1 = Math.max(x1, q.x)), (y1 = Math.max(y1, q.y));
  const k = 1 / Math.max(x1 - x0, y1 - y0, 1e-6);
  const cx = (x0 + x1) / 2,
    cy = (y0 + y1) / 2;
  return polys.map((p) => p.map((q) => ({ x: (q.x - cx) * k, y: (q.y - cy) * k })));
}

// ---------------------------------------------------------------- the stroke

/**
 * Filled polygons for a node's stroke along `outline` (node-local coordinates). `source` resolves
 * custom brush layers to their outlines.
 */
export function drawStrokeGeometry(n: Pick<NodeData, 'id' | 'strokeWeight' | 'strokeCap' | 'strokeWidthProfile' | 'strokeDynamic' | 'strokeBrush'>, outline: PathCmd[], source?: SourceOutline): DrawStroke {
  const w0 = Math.max(0.01, n.strokeWeight);
  const brush: StrokeBrush | undefined = n.strokeBrush;
  const seed = n.strokeBrush?.seed ?? n.strokeDynamic?.seed ?? seedOf(n.id);
  const polygons: Pt[][] = [];
  let rule: DrawStroke['rule'] = 'nonzero';
  const lines = flattenPath(outline, 0.2).filter((l) => l.points.length > 1);
  lines.forEach((line, li) => {
    // a stretch brush can run backwards along the path
    if (brush?.direction === 'BACKWARD') line = { ...line, points: [...line.points].reverse() };
    const sm = resample(line.points, line.closed, Math.max(0.6, Math.min(w0 / 4, 4)));
    let { pts } = sm;
    const { s } = sm;
    const L = s[s.length - 1] || 1;
    // dynamic stroke: move points along the normal by smooth noise
    const dyn = n.strokeDynamic;
    if (dyn && dyn.wiggle > 0) {
      const th = tangents(pts, sm.closed);
      const f = Math.max(0.01, dyn.frequency) / 100;
      pts = pts.map((p, i) => {
        // closed paths: fade the wobble to zero where the loop meets, so it closes cleanly
        const fade = sm.closed ? Math.min(1, s[i] / (L * 0.05 + 1e-6), (L - s[i]) / (L * 0.05 + 1e-6)) : 1;
        // smoothen (0..1) mixes in a slower wave, calming the jitter
        const sm2 = dyn.smoothen ?? 0;
        const d = dyn.wiggle * ((1 - sm2) * noise1(s[i] * f, seed + li) + sm2 * noise1(s[i] * f * 0.35, seed + li + 17)) * fade;
        return { x: p.x - Math.sin(th[i]) * d, y: p.y + Math.cos(th[i]) * d };
      });
    }
    const th = tangents(pts, sm.closed);
    if (brush?.type === 'SCATTER') {
      const shape = brush.sourceId && source ? source(brush.sourceId) : null;
      const unit = shape ? normalize(shape) : [unitShape(brush.preset ?? (brush.name && FIGMA_SCATTER_BRUSHES[brush.name]) ?? 'dots')];
      const size = (brush.size ?? 1) * w0;
      const step = Math.max(0.5, size * (brush.gap ?? 1.5));
      const r = rng(seed + li * 7919);
      const at = (dist: number) => {
        let k = 0;
        while (k < s.length - 1 && s[k + 1] < dist) k++;
        const t = s[k + 1] > s[k] ? (dist - s[k]) / (s[k + 1] - s[k]) : 0;
        const a = pts[k],
          b = pts[Math.min(k + 1, pts.length - 1)];
        return { p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, a: th[k] };
      };
      for (let d = step / 2; d <= L + 1e-6; d += step) {
        const { p, a } = at(d);
        const k = size * Math.max(0.05, 1 + (brush.sizeJitter ?? 0) * (r() * 2 - 1));
        const off = (brush.offsetJitter ?? 0) * w0 * (r() * 2 - 1);
        const rot = (brush.rotateAlong !== false ? a : 0) + ((brush.rotation ?? 0) * Math.PI) / 180 + (brush.angleJitter ?? 0) * Math.PI * (r() * 2 - 1);
        const c = { x: p.x - Math.sin(a) * off, y: p.y + Math.cos(a) * off };
        const cos = Math.cos(rot),
          sin = Math.sin(rot);
        for (const poly of unit) polygons.push(poly.map((q) => ({ x: c.x + (q.x * cos - q.y * sin) * k, y: c.y + (q.x * sin + q.y * cos) * k })));
      }
      return;
    }
    if (brush?.type === 'STRETCH' && brush.sourceId && source) {
      // custom stretch brush: the source's outline bent along the path (x along, y across)
      const shape = source(brush.sourceId);
      if (shape?.length) {
        let x0 = Infinity,
          x1 = -Infinity,
          y0 = Infinity,
          y1 = -Infinity;
        for (const p of shape) for (const q of p) (x0 = Math.min(x0, q.x)), (x1 = Math.max(x1, q.x)), (y0 = Math.min(y0, q.y)), (y1 = Math.max(y1, q.y));
        const sw = x1 - x0 || 1,
          sh = y1 - y0 || 1;
        const along = (dist: number) => {
          let k = 0;
          while (k < s.length - 1 && s[k + 1] < dist) k++;
          const t = s[k + 1] > s[k] ? (dist - s[k]) / (s[k + 1] - s[k]) : 0;
          const a = pts[k],
            b = pts[Math.min(k + 1, pts.length - 1)];
          return { p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, a: th[k] };
        };
        for (const poly of shape) {
          // densify so the bend follows curves
          const dense: Pt[] = [];
          for (let i = 0; i < poly.length; i++) {
            const a = poly[i],
              b = poly[(i + 1) % poly.length];
            const steps = Math.max(1, Math.ceil((Math.abs(b.x - a.x) / sw) * 64));
            for (let j = 0; j < steps; j++) dense.push({ x: a.x + ((b.x - a.x) * j) / steps, y: a.y + ((b.y - a.y) * j) / steps });
          }
          polygons.push(
            dense.map((q) => {
              const { p, a } = along(((q.x - x0) / sw) * L);
              const v = ((q.y - (y0 + y1) / 2) / sh) * w0 * profileAt(n.strokeWidthProfile, (q.x - x0) / sw);
              return { x: p.x - Math.sin(a) * v, y: p.y + Math.cos(a) * v };
            }),
          );
        }
        return;
      }
    }
    // a ribbon whose width follows the profile and the brush
    const preset = brush?.type === 'STRETCH' ? (brush.preset ?? (brush.name && FIGMA_STRETCH_BRUSHES[brush.name]) ?? 'marker') : null;
    const profile = n.strokeWidthProfile?.length ? n.strokeWidthProfile : preset === 'taper' || preset === 'ink' ? WIDTH_PROFILES['mirrored-taper'] : undefined;
    const nib = ((brush?.angle ?? 45) * Math.PI) / 180;
    const width = pts.map((_, i) => {
      let w = w0 * profileAt(profile, s[i] / L);
      if (preset === 'calligraphy') w *= 0.15 + 0.85 * Math.abs(Math.sin(th[i] - nib));
      else if (preset === 'marker') w *= 0.92 + 0.08 * noise1(s[i] / 20, seed);
      else if (preset === 'ink') w *= 0.75 + 0.35 * (noise1(s[i] / 14, seed) * 0.5 + 0.5);
      return Math.max(0, w);
    });
    const left = pts.map((p, i) => ({ x: p.x - (Math.sin(th[i]) * width[i]) / 2, y: p.y + (Math.cos(th[i]) * width[i]) / 2 }));
    const right = pts.map((p, i) => ({ x: p.x + (Math.sin(th[i]) * width[i]) / 2, y: p.y - (Math.cos(th[i]) * width[i]) / 2 }));
    if (sm.closed) {
      // a ring: both edges as loops, filled even-odd
      polygons.push(left, right.reverse());
      rule = 'evenodd';
      return;
    }
    const cap = (c: Pt, a: number, w: number, start: boolean): Pt[] => {
      if (n.strokeCap !== 'ROUND' || w <= 0) return [];
      const out: Pt[] = [];
      for (let k = 1; k < 8; k++) {
        const ang = a + (start ? Math.PI / 2 : -Math.PI / 2) + (k / 8) * Math.PI;
        out.push({ x: c.x + (Math.cos(ang) * w) / 2, y: c.y + (Math.sin(ang) * w) / 2 });
      }
      return out;
    };
    const last = pts.length - 1;
    polygons.push([...left, ...cap(pts[last], th[last], width[last], false), ...right.reverse(), ...cap(pts[0], th[0], width[0], true)]);
  });
  return { polygons, rule };
}

/** How far a Figma Draw stroke can reach beyond its path (for bounds). */
export function drawStrokeReach(n: Pick<NodeData, 'strokeWeight' | 'strokeWidthProfile' | 'strokeDynamic' | 'strokeBrush'>): number {
  const w0 = n.strokeWeight;
  const prof = Math.max(1, ...(n.strokeWidthProfile ?? []).map((p) => p.width));
  const b = n.strokeBrush;
  const scatter = b?.type === 'SCATTER' ? (b.size ?? 1) * (1 + (b.sizeJitter ?? 0)) * 0.75 + (b.offsetJitter ?? 0) : 0;
  return w0 * Math.max(prof / 2, scatter) + (n.strokeDynamic?.wiggle ?? 0);
}

/** SVG path data for polygons. */
export function polygonsToSvg(polys: Pt[][]): string {
  const f = (v: number) => Math.round(v * 100) / 100;
  return polys.map((p) => (p.length ? `M${p.map((q) => `${f(q.x)} ${f(q.y)}`).join('L')}Z` : '')).join('');
}
